import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Button,
  Alert,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import _ from "lodash";
import moment from "moment";
import axios from "axios";
import Utils from "./../../common/Utils";
import DateTimePicker from "react-datetime-picker";
import { UserContext } from "../../common";
import CheckInOut from "./CheckInOut";
import DailyDeclaration from "./DailyDeclaration";
import KeyEmployeeRules from "./KeyEmployeeRules";

const greeting = () => {
  const h = moment().hour();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const DateWise = (props) => {
  const { userData } = useContext(UserContext);
  const [marking, setMarking] = useState(false);
  const [members, setMembers] = useState([]);
  const [backdatedOpen, setBackdatedOpen] = useState(false);
  const [attnType, setAttnType] = useState("paid");
  const [attnDate, setAttnDate] = useState(new Date());
  const [viewLogs, showViewLogs] = useState(false);
  const [logs, setLogs] = useState({ loading: false, data: [], ontime: true });
  const [liveStatus, setLiveStatus] = useState(null);
  const [workingElapsed, setWorkingElapsed] = useState("");
  const [freshAid, setFreshAid] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [showDeclModal, setShowDeclModal] = useState(false);
  const [expandedReasons, setExpandedReasons] = useState({});
  const rulesRef = useRef(null);

  const refReason = useRef(null);
  const refType = useRef(null);
  const refDur = useRef(null);
  const refMember = useRef(null);
  const refMember2 = useRef(null);

  const fetchMembers = () => {
    axios
      .get(Utils.apiUrl("users/fetchMembers"), Utils.apiHeaders())
      .then((res) => {
        const data = Utils.parseApiResponse(res);
        setMembers(data.members || []);
      })
      .catch(() => { });
  };

  const fetchLiveStatus = () => {
    axios
      .get(Utils.apiUrl("attendence/livestatus"), Utils.apiHeaders())
      .then((res) => {
        const data = Utils.parseApiResponse(res);
        if (data.success) setLiveStatus(data.data);
      })
      .catch(() => { });
  };

  // On mount
  useEffect(() => {
    if (userData.manager || userData.is_super_user) {
      fetchMembers();
    }
    fetchLiveStatus();
  }, []); // eslint-disable-line

  // Sync when refreshTrigger changes
  useEffect(() => {
    fetchLiveStatus();
    if (viewLogs) fetchLogs();
  }, [props.refreshTrigger]); // eslint-disable-line

  // Sync freshAid when props.aid changes (e.g. after parent reloads)
  useEffect(() => {
    if (props.aid > 0) setFreshAid(props.aid);
  }, [props.aid]);

  // Live elapsed timer
  useEffect(() => {
    if (!liveStatus || !liveStatus.check_in_time || liveStatus.check_out_time) {
      setWorkingElapsed("");
      return;
    }
    const tick = () => {
      const diff = moment().diff(moment(liveStatus.check_in_time), 'seconds');
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      setWorkingElapsed(`${h}h ${m < 10 ? "0" + m : m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [liveStatus]);

  const fetchLogs = () => {
    setLogs((l) => ({ ...l, loading: true }));
    axios
      .get(
        Utils.apiUrl(
          `attendence/fetchlogs?member=${refMember2.current ? refMember2.current.value : userData.uid}`
        ),
        Utils.apiHeaders()
      )
      .then((res) => {
        const data = Utils.parseApiResponse(res);
        setLogs((l) => ({ ...l, loading: false, data: data.data.logs }));
      })
      .catch(() => {
        setLogs((l) => ({ ...l, loading: false, data: [] }));
      });
  };

  useEffect(() => {
    if (viewLogs) fetchLogs();
  }, [viewLogs]); // eslint-disable-line

  // Close the Key Employee Rules popup on outside click
  useEffect(() => {
    if (!showRules) return;
    const onClickOutside = (e) => {
      if (rulesRef.current && !rulesRef.current.contains(e.target)) {
        setShowRules(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showRules]);

  const deleteAttn = (id) => {
    if (window.confirm("You are going to delete this attendence, Continue?")) {
      setLogs((l) => ({ ...l, loading: true }));
      axios
        .post(Utils.apiUrl("attendence/deleteattn"), `id=${id}`, Utils.apiHeaders())
        .then((res) => {
          const data = Utils.parseApiResponse(res);
          if (data.success) {
            fetchLogs();
            if (props.onRefresh) props.onRefresh();
          } else {
            window.alert(data.message);
            setLogs((l) => ({ ...l, loading: false }));
          }
        })
        .catch(() => setLogs((l) => ({ ...l, loading: false })));
    }
  };

  // Ensures today's "mark" record (ontime=1) exists before check-in — same
  // attendence/mark call the app already used, just triggered inline by the
  // START WORK button instead of via a separate "Save Attendance" step.
  const ensureAttendanceMarked = async () => {
    if (freshAid > 0) return freshAid;
    if (props.aid > 0) return props.aid;
    try {
      const data = `&ts=${moment().format("YYYY-MM-DD HH:mm:ss")}&ontime=1`;
      const res = await axios.post(Utils.apiUrl("attendence/mark"), data, Utils.apiHeaders());
      const resp = Utils.parseApiResponse(res);
      if (resp.aid > 0) {
        setFreshAid(resp.aid);
        if (props.onMark) props.onMark(resp.aid);
        return resp.aid;
      }
      if (resp.success) {
        return 1;
      }
      throw new Error(resp.message || "Could not start attendance.");
    } catch (e) {
      // If error occurs, still allow checkin to proceed as backend auto-provisions
      return 0;
    }
  };

  const markBackdated = () => (e) => {
    if (!refReason.current || refReason.current.value === "") {
      alert("Please enter the Reason!");
      return;
    }
    if (!refType.current || refType.current.value === "") {
      alert("Please select Type!");
      return;
    }
    if (!refDur.current || refDur.current.value === "") {
      alert("Please select Duration!");
      return;
    }
    let data = `&ts=${moment(attnDate).format("YYYY-MM-DD HH:mm:ss")}&ontime=0`;
    data += `&diftype=${refType.current.value}&duration=${refDur.current.value}`;
    data += `&reason=${refReason.current.value}&member=${refMember.current ? refMember.current.value : ""}`;

    setMarking(true);
    axios
      .post(Utils.apiUrl("attendence/mark"), data, Utils.apiHeaders())
      .then((res) => {
        const resp = Utils.parseApiResponse(res);
        if (resp.success) {
          setBackdatedOpen(false);
          if (props.onMark) props.onMark(resp.aid);
          if (props.onRefresh) props.onRefresh();
        } else {
          alert(resp.message);
        }
      })
      .catch(() => { })
      .finally(() => setMarking(false));
  };

  const closeBackdated = () => setBackdatedOpen(false);

  const toggleReason = (id) => {
    setExpandedReasons((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderReason = (rec) => {
    const reason = (rec.reason || "").trim();
    if (!reason) {
      return <span className="text-muted">—</span>;
    }
    if (reason.length <= 20) {
      return <span>{reason}</span>;
    }
    const isExpanded = !!expandedReasons[rec.id];
    return (
      <div style={{ maxWidth: isExpanded ? "360px" : "220px", whiteSpace: isExpanded ? "normal" : "nowrap" }}>
        <span>{isExpanded ? reason : `${reason.slice(0, 20)}...`}</span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1 text-primary fw-semibold"
          style={{ fontSize: "12px", textDecoration: "none", verticalAlign: "baseline", cursor: "pointer" }}
          onClick={() => toggleReason(rec.id)}
        >
          {isExpanded ? "Read Less" : "Read More"}
        </button>
      </div>
    );
  };

  const renderBackdated = () => (
    <Modal show={true} size="md" onHide={closeBackdated}>
      <Modal.Header closeButton>
        <b>Log Backdated Hours</b>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mt-2 px-4">
          <div className="m-2">
            <DateTimePicker
              disableClock={true}
              clearIcon={null}
              format="y-MM-dd"
              minDate={moment().subtract(props.attnLogDays, "days").toDate()}
              maxDate={moment().toDate()}
              onChange={setAttnDate}
              value={attnDate}
              className="mx-2"
            />
          </div>

          {members.length > 0 && (
            <select className="form-select my-2" ref={refMember}>
              <option value={userData.uid}>Self</option>
              {_.map(members, (m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
          <input
            type="text"
            className="form-control"
            placeholder="Reason for Differed Time"
            ref={refReason}
          />
          <div className="d-flex mt-2 gap-2">
            <select
              className="form-select"
              ref={refType}
              onChange={(e) => setAttnType(e.currentTarget.value)}
            >
              <option value="paid">Overtime</option>
              <option value="unpaid">UnPaid</option>
              <option value="lieu">Time In Lieu</option>
            </select>
            <select className="form-select" ref={refDur}>
              {attnType === "paid" &&
                _.map(_.fill(new Array(48), 0), (v, idx2) => (
                  <option key={idx2} value={(idx2 + 1) / 4}>
                    {((idx2 + 1) / 4).toFixed(2)} Hrs.
                  </option>
                ))}
              {attnType === "unpaid" &&
                _.map(_.fill(new Array(48), 0), (v, idx2) => (
                  <option key={idx2 - 48} value={(idx2 - 48) / 4}>
                    {((idx2 - 48) / 4).toFixed(2)} Hrs.
                  </option>
                ))}
              {attnType === "lieu" &&
                _.map(_.fill(new Array(97), 0), (v, idx2) => (
                  <option key={idx2 - 48} value={(idx2 - 48) / 4}>
                    {((idx2 - 48) / 4).toFixed(2)} Hrs.
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="text-center mt-3">
          <Button className="mx-1" variant="secondary" onClick={closeBackdated}>
            Cancel
          </Button>
          <Button className="mx-1" variant="primary" disabled={marking} onClick={markBackdated()}>
            {marking ? "Saving..." : "Save Attendance"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );

  const logrecords = () =>
    _.filter(logs.data, (d) => d.ontime === (logs.ontime ? "1" : "0"));

  const renderViewLogs = () => (
    <Modal show={true} size="lg" onHide={() => showViewLogs(false)}>
      <Modal.Header closeButton>
        <b>View Attendance Logs (last {props.attnLogDays} days)</b>
        <div className="flex-fill px-4">
          {members.length > 0 && (
            <select
              className="form-select my-2"
              ref={refMember2}
              onChange={fetchLogs}
            >
              <option value={userData.uid}>Self</option>
              {_.map(members, (m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {logs.loading && (
          <Alert variant="warning" className="m-0 py-2">
            <ProgressBar variant="info" animated now={100} />
          </Alert>
        )}
        {!logs.loading && (
          <>
            <ul className="nav nav-pills nav-justified">
              <li className="nav-item">
                <a
                  className={`nav-link ${logs.ontime ? "active" : ""}`}
                  href="#normal"
                  onClick={(e) => { e.preventDefault(); setLogs((l) => ({ ...l, ontime: true })); }}
                >
                  Normal Hours
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${!logs.ontime ? "active" : ""}`}
                  href="#differed"
                  onClick={(e) => { e.preventDefault(); setLogs((l) => ({ ...l, ontime: false })); }}
                >
                  Differed Hours
                </a>
              </li>
            </ul>
            {logrecords().length === 0 && (
              <div className="alert alert-warning p-2 my-3 text-center">
                No attendance found!
              </div>
            )}
            {logrecords().length > 0 && (
              <div className="table my-3 table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr className="bg-light text-dark">
                      <th>Marked On</th>
                      <th>Time</th>
                      <th>Duration</th>
                      {logs.ontime && (
                        <>
                          <th>Check-In</th>
                          <th>Check-Out</th>
                          <th>Working Hrs</th>
                          <th>Normal Hrs</th>
                          <th>Deferred Hrs</th>
                          <th>Payroll Hrs</th>
                          <th>Selfie</th>
                          <th>Location</th>
                        </>
                      )}
                      {!logs.ontime && (
                        <>
                          <th>Type</th>
                          <th>Reason</th>
                          <th className="text-center">Approved?</th>
                        </>
                      )}
                      <th className="text-center" style={{ width: 40 }}>
                        <img
                          src={`${process.env.PUBLIC_URL}/images/delete.png`}
                          alt="Delete"
                          style={{ width: 15 }}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logrecords().map((rec, idx) => {
                      // Calculate Normal Hours
                      const normalHours = rec.ontime === "1" ? rec.duration : 0;

                      // Calculate Deferred Hours
                      const deferredHours = rec.ontime === "0" ? rec.duration : 0;

                      // Calculate Payroll Hours
                      let payrollHours = 0;
                      if (rec.ontime === "1") {
                        payrollHours = rec.duration;
                      } else if (rec.ontime === "0") {
                        if (rec.diftype === 'paid') {
                          payrollHours = rec.duration;
                        } else if (rec.diftype === 'unpaid') {
                          payrollHours = -rec.duration;
                        }
                      }

                      return (
                        <tr key={idx}>
                          <td>
                            {moment(rec.login).format("YYYY-MM-DD")}{" "}
                            {_.get(rec, "markedBy", "") !== "" && (
                              <span className="badge bg-dark text-white">
                                {rec.markedBy}
                              </span>
                            )}
                          </td>
                          <td>{moment(rec.login).format("hh:mm A")}</td>
                          <td>{rec.duration} Hrs.</td>
                          {logs.ontime && (
                            <>
                              <td>
                                {rec.check_in_time
                                  ? moment(rec.check_in_time).format("hh:mm A")
                                  : "—"}
                              </td>
                              <td>
                                {rec.check_out_time
                                  ? moment(rec.check_out_time).format("hh:mm A")
                                  : "—"}
                              </td>
                              <td>{rec.working_hours ?? "—"}</td>
                              <td>{normalHours > 0 ? Utils.formatWorkingHours(normalHours) : "—"}</td>
                              <td>{deferredHours > 0 ? Utils.formatWorkingHours(deferredHours) : "—"}</td>
                              <td>{payrollHours !== 0 ? (payrollHours < 0 ? `-${Utils.formatWorkingHours(Math.abs(payrollHours))}` : Utils.formatWorkingHours(payrollHours)) : "—"}</td>
                              <td>
                                {rec.check_in_image ? (
                                  <img
                                    src={rec.check_in_image}
                                    alt="in"
                                    style={{
                                      width: 32, height: 32, objectFit: "cover",
                                      borderRadius: 4, cursor: "pointer",
                                    }}
                                    onClick={() => Utils.viewImage(rec.check_in_image)}
                                  />
                                ) : "—"}
                                {rec.check_out_image && (
                                  <img
                                    src={rec.check_out_image}
                                    alt="out"
                                    style={{
                                      width: 32, height: 32, objectFit: "cover",
                                      borderRadius: 4, cursor: "pointer", marginLeft: 4,
                                    }}
                                    onClick={() => Utils.viewImage(rec.check_out_image)}
                                  />
                                )}
                              </td>
                              <td>
                                {rec.location_status && <div>In: {rec.location_status}</div>}
                                {rec.checkout_location_status && <div>Out: {rec.checkout_location_status}</div>}
                                {!rec.location_status && !rec.checkout_location_status && "—"}
                              </td>
                            </>
                          )}
                          {!logs.ontime && (
                            <>
                              <td>{rec.diftype}</td>
                              <td>{renderReason(rec)}</td>
                              <td className="text-center">
                                {rec.approved === "1" ? "Yes" : "No"}
                              </td>
                            </>
                          )}
                          <td className="text-center">
                            <img
                              src={`${process.env.PUBLIC_URL}/images/delete.png`}
                              className="clickable"
                              onClick={() => deleteAttn(rec.id)}
                              title="Click to Delete"
                              style={{ width: 15 }}
                              alt="del"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );

  const checkedOut = !!(liveStatus && liveStatus.check_out_time);
  const activeAid = freshAid > 0 ? freshAid : props.aid;

  return (
    <>
      {backdatedOpen && renderBackdated()}
      {viewLogs && renderViewLogs()}

      {showDeclModal && (
        <Modal show={showDeclModal} onHide={() => setShowDeclModal(false)} centered size="lg">
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="h6 fw-bold m-0" style={{ color: "var(--dt-navy)" }}>
              <i className="bi bi-clock-history text-primary me-2" />
              Shift Completed — End-of-Day Hours Declaration
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-md-4">
            <div className="alert alert-info py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2">
              <i className="bi bi-info-circle-fill fs-5 text-info" />
              <div>
                <strong>Shift Clock-Out Recorded!</strong> Do you have any <strong>Extra Hours (Overtime)</strong>, <strong>Minus Hours</strong>, or <strong>Time In Lieu</strong> to declare for today?
              </div>
            </div>
            <DailyDeclaration
              checkedOut={true}
              refreshTrigger={props.refreshTrigger}
              onSuccess={() => {
                fetchLiveStatus();
                fetchLogs();
                if (props.onRefresh) props.onRefresh();
              }}
            />
          </Modal.Body>
          <Modal.Footer className="bg-light py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDeclModal(false)}>
              Close / Standard Shift (No Extra Hours)
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="card shadow-sm border mb-4 bg-white" style={{ borderRadius: "14px", border: "1.5px solid #CBD5E1" }}>
        <div className="card-body p-3 p-sm-4">
          <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap pb-3 border-bottom">
            <div>
              <div className="text-muted small fw-semibold">
                {greeting()}{userData?.name ? `, ${userData.name}` : ""}
              </div>
              <h4 className="m-0 fw-bold" style={{ color: "var(--dt-navy)", fontSize: "18px" }}>
                Employee Daily Attendance Flow
              </h4>
            </div>
            <div className="d-flex gap-2 position-relative flex-wrap w-100 w-sm-auto justify-content-start justify-content-sm-end mt-2 mt-sm-0" ref={rulesRef}>
              <Button
                variant={showRules ? "warning" : "outline-warning"}
                size="sm"
                className="fw-semibold px-3 rounded-pill flex-fill flex-sm-grow-0"
                onClick={() => setShowRules((s) => !s)}
              >
                🛡️ Rules
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="fw-semibold px-3 rounded-pill flex-fill flex-sm-grow-0"
                onClick={() => setBackdatedOpen(true)}
              >
                📅 Backdated
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                className="fw-semibold px-3 rounded-pill flex-fill flex-sm-grow-0"
                onClick={() => showViewLogs(true)}
              >
                📋 Logs
              </Button>
              {showRules && <KeyEmployeeRules onClose={() => setShowRules(false)} />}
            </div>
          </div>

          <div className="row g-3">
            {/* Card 1/2 — Check-In / Check-Out */}
            <div className="col-12 col-lg-6">
              <div className="p-3 h-100 rounded-3 border bg-white shadow-sm" style={{ borderColor: "#CBD5E1" }}>
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill bg-primary px-2 py-1" style={{ fontSize: "11px", fontWeight: 700 }}>
                      STEP 1 &amp; 2
                    </span>
                    <span className="fw-bold text-dark" style={{ fontSize: "13.5px" }}>Daily Clock In / Out</span>
                  </div>
                  <span className="badge bg-light text-secondary border px-2 py-1" style={{ fontSize: "11px" }}>
                    {liveStatus?.check_out_time ? 'COMPLETED' : (liveStatus?.check_in_time ? 'ACTIVE SHIFT' : 'READY')}
                  </span>
                </div>
                <CheckInOut
                  attnId={activeAid}
                  existingStatus={liveStatus}
                  onStartWork={ensureAttendanceMarked}
                  onOpenDeclaration={() => setShowDeclModal(true)}
                  onCheckin={(data) => {
                    if (data?.aid) {
                      setFreshAid(data.aid);
                      if (props.onMark) props.onMark(data.aid);
                    }
                    fetchLiveStatus();
                    if (props.onRefresh) props.onRefresh();
                  }}
                  onCheckout={() => {
                    fetchLiveStatus();
                    setShowDeclModal(true);
                    if (props.onRefresh) props.onRefresh();
                  }}
                />
              </div>
            </div>

            {/* Card 3 — Daily Declaration */}
            <div className="col-12 col-lg-6">
              <div className="p-3 h-100 rounded-3 border bg-white shadow-sm" style={{ borderColor: "#CBD5E1" }}>
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill px-2 py-1 text-white" style={{ fontSize: "11px", fontWeight: 700, backgroundColor: "#0284C7" }}>
                      STEP 3
                    </span>
                    <span className="fw-bold text-dark" style={{ fontSize: "13.5px" }}>End-of-Day Declaration</span>
                  </div>
                  <span className="badge bg-light text-secondary border px-2 py-1" style={{ fontSize: "11px" }}>
                    {checkedOut ? 'AVAILABLE' : 'PENDING CLOCK-OUT'}
                  </span>
                </div>
                <DailyDeclaration
                  checkedOut={checkedOut}
                  refreshTrigger={props.refreshTrigger}
                  onSuccess={() => {
                    fetchLiveStatus();
                    fetchLogs();
                    if (props.onRefresh) props.onRefresh();
                  }}
                />
              </div>
            </div>
          </div>

          {marking && (
            <Alert variant="warning" className="mt-3 mb-0 py-1">
              <ProgressBar variant="info" animated now={100} style={{ width: 100 }} />
            </Alert>
          )}

          {/* Modern KPI Summary Tiles */}
          <div className="row g-2 text-center mt-3 pt-3 border-top">
            {/* Check-In */}
            <div className="col-6 col-md">
              <div className="p-2 rounded-3 bg-light border">
                <div className="text-muted small mb-1" style={{ fontSize: "11px", fontWeight: 600 }}>
                  <i className="bi bi-box-arrow-in-right text-success me-1" />Check-In
                </div>
                <div className="fw-bold num" style={{ fontSize: "16px", color: "#10B981" }}>
                  {liveStatus && liveStatus.check_in_time
                    ? moment(liveStatus.check_in_time).format("hh:mm A")
                    : "—"}
                </div>
              </div>
            </div>

            {/* Check-Out */}
            <div className="col-6 col-md">
              <div className="p-2 rounded-3 bg-light border">
                <div className="text-muted small mb-1" style={{ fontSize: "11px", fontWeight: 600 }}>
                  <i className="bi bi-box-arrow-right text-danger me-1" />Check-Out
                </div>
                <div className="fw-bold num" style={{ fontSize: "16px", color: "#EF4444" }}>
                  {liveStatus && liveStatus.check_out_time
                    ? moment(liveStatus.check_out_time).format("hh:mm A")
                    : "—"}
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="col-6 col-md">
              <div className="p-2 rounded-3 bg-light border">
                <div className="text-muted small mb-1" style={{ fontSize: "11px", fontWeight: 600 }}>
                  <i className="bi bi-hourglass-split text-primary me-1" />Working Hours
                </div>
                <div className="fw-bold num text-dark" style={{ fontSize: "16px" }}>
                  {liveStatus && liveStatus.check_in_time
                    ? (liveStatus.working_hours
                      ? Utils.formatWorkingHours(liveStatus.working_hours)
                      : workingElapsed || "—")
                    : "—"}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="col-6 col-md">
              <div className="p-2 rounded-3 bg-light border">
                <div className="text-muted small mb-1" style={{ fontSize: "11px", fontWeight: 600 }}>
                  <i className="bi bi-geo-alt text-secondary me-1" />Location
                </div>
                <div className="fw-semibold text-secondary text-truncate" style={{ fontSize: "13px" }}>
                  {liveStatus && liveStatus.location_status
                    ? (liveStatus.checkout_location_status && liveStatus.checkout_location_status !== liveStatus.location_status
                      ? `In: ${liveStatus.location_status} · Out: ${liveStatus.checkout_location_status}`
                      : liveStatus.location_status)
                    : "—"}
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="col-12 col-md">
              <div className="p-2 rounded-3 bg-light border d-flex flex-column align-items-center justify-content-center">
                <div className="text-muted small mb-1" style={{ fontSize: "11px", fontWeight: 600 }}>Status</div>
                <div>
                  {liveStatus && liveStatus.check_in_time ? (
                    liveStatus.check_out_time ? (
                      <span className="badge bg-success px-2 py-1">Checked Out</span>
                    ) : liveStatus.missing_checkout == 1 ? (
                      <span className="badge bg-danger px-2 py-1">Missing Checkout</span>
                    ) : (
                      (() => {
                        const checkInTime = moment(liveStatus.check_in_time);
                        const limit = moment(liveStatus.check_in_time).set({ hour: 9, minute: 15, second: 0 });
                        if (checkInTime.isAfter(limit)) {
                          return <span className="badge bg-warning text-dark px-2 py-1">Late</span>;
                        }
                        return <span className="badge bg-primary px-2 py-1">Working</span>;
                      })()
                    )
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DateWise;
